import i18next from 'i18next';

import { skipNextModalHistoryCleanup } from 'src/hooks/use-modal-back-handler';

import { openModal } from 'src/utils/modalUtils';
import { pushLearnerError } from 'src/utils/analytics';
import localStorageUtil from 'src/utils/localStorageUtil';
import { makeRedirectUrl } from 'src/utils/redirect-url-util';
import { getTenantRedirectUri } from 'src/utils/multi-tenant-uri';
import { getRuntimeConfig } from 'src/utils/configUtil/configUtil';

import { showAppSnackbar } from 'src/components/snackbar/provider';

/** API 에러 응답 body (axios response.data) */
type ApiErrorResponseBody = {
  meta?: {
    errCode?: number;
    errMsg?: string;
    errTranslate?: string;
  };
  errCode?: number;
};

/**
 * [공통 API 에러 핸들러]
 * 주요 역할:
 * - 전역적으로 발생하는 API 에러에 대한 처리
 * - 사용자에게 보여줄 메시지 제어 및 로그 수집 (Sentry)
 *
 * 동작 흐름:
 * 1. Sentry에 에러 전송 (단, 401은 제외)
 * 2. 응답이 없는 경우 → 네트워크 에러 처리 후 종료
 * 3. 커스텀 에러코드 필터링 → 특정 에러코드는 UI에 노출하지 않고 무시
 * 4. 401 → 토큰 삭제 + 로그인 리디렉트
 * 5. 403, 404 → 일부 API에 대해서는 예외처리 (다이얼로그 노출 안함)
 * 6. 429 → Too many requests → 무시
 * 7. 그 외 → 공통 에러 모달 노출
 * 8. 상태코드 기반 로그 출력
 */
/**
 * 알려진 HTTP/비즈니스 예외를 처리하고, 이미 처리된 경우 반환값을 돌려준다.
 * `undefined`이면 호출부에서 공통 모달·로깅으로 이어간다.
 */
// eslint-disable-next-line @emcast/custom-rules/no-any-in-api
function resolveKnownApiErrorHandling(
  status: number,
  // eslint-disable-next-line @emcast/custom-rules/no-any-in-api
  data: any,
  apiUrl: string,
  apiMethod: string,
  pathname: string,
  search: string
): boolean | undefined {
  if (isDegreeDetailExcept(status, apiUrl, data)) return true;
  if (isSpecificErrorCode(data?.meta?.errCode || data?.errCode)) return false;
  if (is401Exception(status)) return true;

  const privateArticleResult = tryHandlePrivateArticle403(status, apiUrl, data);
  if (privateArticleResult !== undefined) return privateArticleResult;

  const checklistResult = tryHandleChecklistSupervisor403(status, apiUrl, data);
  if (checklistResult !== undefined) return checklistResult;

  const commentDailyLimitResult = tryHandleCommentDailyLimit403(status, apiUrl, apiMethod, data);
  if (commentDailyLimitResult !== undefined) return commentDailyLimitResult;

  const deletedLessonContentResult = tryHandleDeletedLessonContent(status, apiUrl, pathname);
  if (deletedLessonContentResult !== undefined) return deletedLessonContentResult;

  const deletedInboxLearnDetailResult = tryHandleDeletedInboxLearnDetail(
    status,
    apiUrl,
    apiMethod,
    pathname,
    search
  );
  if (deletedInboxLearnDetailResult !== undefined) return deletedInboxLearnDetailResult;

  if (is403Exception(status, apiUrl, pathname)) return true;
  if (is404Exception(status, apiUrl, pathname)) return true;
  if (status === 429) return true;
  return undefined;
}

const getErrorCode = (data: ApiErrorResponseBody | undefined) =>
  data?.meta?.errCode ?? data?.errCode;

// GA4에는 query string이 포함된 전체 URL을 보내지 않습니다.
// 검색어, 토큰, 리다이렉트 경로 같은 값이 query에 섞일 수 있어 path만 남깁니다.
const sanitizeApiPath = (apiUrl: string) => {
  try {
    const parsedUrl = new URL(apiUrl, window.location.origin);
    return parsedUrl.pathname;
  } catch {
    return apiUrl.split('?')[0] || '-';
  }
};

/** 체크리스트 슈퍼바이저 전용(3003) 403 — 안내 후 이전 화면 */
function tryHandleChecklistSupervisor403(
  status: number,
  apiUrl: string,
  data: ApiErrorResponseBody
): boolean | undefined {
  if (status !== 403) return undefined;
  if (!isChecklistSupervisorOnlyError(apiUrl, data)) return undefined;
  openModal('alert', {
    content:
      data?.meta?.errMsg ??
      data?.meta?.errTranslate ??
      '이 체크리스트는 슈퍼바이저만 작성할 수 있습니다.',
    accept: '확인',
    onAccept: () => {
      if (typeof window !== 'undefined' && window.history.length > 1) {
        window.history.back();
      }
    },
  });
  return false;
}

/**
 * 비공개 게시글 접근 차단 (403 + errCode 6310).
 * 커뮤니티 article API path에만 적용한다. 확인 시 해당 보드 목록으로 replace 한다.
 */
function isCommunityArticleApiPath(apiUrl: string) {
  const path = getApiPath(apiUrl);
  return /\/board\/[^/]+\/article\/\d+/.test(path);
}

function replaceCommunityListFromArticleApi(apiUrl: string) {
  const path = getApiPath(apiUrl);
  const match = /\/board\/([^/]+)\/article\/\d+/.exec(path);
  skipNextModalHistoryCleanup();
  if (match?.[1]) {
    window.location.replace(`${getCurrentRouteBasePath()}/community/${match[1]}`);
    return;
  }
  if (typeof window !== 'undefined' && window.history.length > 1) {
    window.history.back();
  }
}

function tryHandlePrivateArticle403(
  status: number,
  apiUrl: string,
  data: ApiErrorResponseBody
): boolean | undefined {
  if (status !== 403) return undefined;
  const errCode = data?.meta?.errCode ?? data?.errCode;
  if (errCode !== 6310) return undefined;
  if (!isCommunityArticleApiPath(apiUrl)) return undefined;

  openModal('alert', {
    title: i18next.t('learner.5031'),
    content: i18next.t('learner.5032'),
    accept: i18next.t('learner.5014'),
    disableBackdropClick: true,
    onAccept: () => {
      replaceCommunityListFromArticleApi(apiUrl);
    },
  });
  return true;
}

/**
 * 커뮤니티·소셜 댓글/답글 POST 일일 한도(403 + errCode 6305).
 * path에 /checklist/ 또는 /instant/가 있으면 전용 분기에 넣지 않는다.
 */
function tryHandleCommentDailyLimit403(
  status: number,
  apiUrl: string,
  apiMethod: string,
  data: ApiErrorResponseBody
): boolean | undefined {
  if (status !== 403) return undefined;
  if (apiMethod !== 'POST') return undefined;
  const errCode = data?.meta?.errCode ?? data?.errCode;
  if (errCode === 6305) {
    const path = getApiPath(apiUrl);
    if (path.includes('/checklist/') || path.includes('/instant/')) return undefined;
    if (!/\/board\/[^/]+\/article\/\d+\/comment(\/\d+)?$/.test(path)) return undefined;

    showAppSnackbar(i18next.t('learner.3064'), { variant: 'default' });
    return true;
  }
  return undefined;
}

/**
 * 인박스의 삭제된 필수 배정 차수 링크 처리.
 * 삭제된 차수 URL(`/learn/:learnId/lesson/:lessonId/progression/:progressionId`)로 진입하면
 * 학습창이 차시 콘텐츠/인트로/타임라인 API를 호출한 뒤 400/404를 받을 수 있다.
 * 이 케이스는 시스템 장애가 아니라 "관리자가 삭제해 더 이상 확인할 수 없는 콘텐츠" 안내가 맞으므로
 * 공통 `API 오류입니다.` 모달로 보내지 않고, 전용 다국어 안내 후 확인 시 이전 화면으로 복귀시킨다.
 */
function tryHandleDeletedLessonContent(
  status: number,
  apiUrl: string,
  pathname: string
): boolean | undefined {
  if (status !== 400 && status !== 404) return undefined;
  if (!isLearningContentRoute(pathname)) return undefined;
  if (!isLearningContentApi(apiUrl)) return undefined;

  openModal('alert', {
    content: i18next.t('learner.5013'),
    accept: i18next.t('learner.5014'),
    disableBackdropClick: true,
    onAccept: () => {
      // 모바일은 inbox로 보내고, 데스크탑은 home으로 보낸다.
      if (isMobileViewport()) {
        goBackToInboxEntryFromDeletedInboxRoute();
      } else {
        replaceAppRoute('/home');
      }
    },
  });

  return true;
}

function isLearningContentRoute(pathname: string) {
  return /^\/(?:[^/]+\/)?learn\/[^/]+\/lesson\/[^/]+\/progression\/[^/]+$/.test(pathname);
}

function isLearningContentApi(apiUrl: string) {
  if (!apiUrl.includes('/learn/learner/online/')) return false;
  if (!apiUrl.includes('/lesson/')) return false;

  return (
    apiUrl.includes('/contents') ||
    apiUrl.includes('/intro') ||
    apiUrl.includes('/timelinecontents')
  );
}

/**
 * 인박스에서 삭제된 나의 클래스 상세로 들어온 케이스 처리.
 * 인박스 링크는 `/learn/:learnId?entry=inbox`로 진입 경로를 남기므로,
 * 이 값이 있을 때만 삭제된 강의/차수에서 발생하는 상세 진입 API 실패를 전용 안내로 전환한다.
 *
 * 여기서 `500 /start`를 전역 예외로 잡지 않는 것이 중요하다.
 * 같은 500이라도 일반 서버 장애일 수 있으므로, 인박스 유입 + 나의 클래스 상세 화면 + 특정 learn API 조합일 때만
 * "확인할 수 없는 콘텐츠" 안내 후 인박스로 복귀시킨다.
 */
function tryHandleDeletedInboxLearnDetail(
  status: number,
  apiUrl: string,
  apiMethod: string,
  pathname: string,
  search: string
): boolean | undefined {
  if (!search.includes('entry=inbox')) return undefined;
  if (!isLearnDetailRoute(pathname)) return undefined;
  if (!isDeletedInboxLearnDetailApi(status, apiUrl, apiMethod)) return undefined;

  openModal('alert', {
    content: i18next.t('learner.5013'),
    accept: i18next.t('learner.5014'),
    disableBackdropClick: true,
    onAccept: () => {
      // 모바일은 inbox로 보내고, 데스크탑은 home으로 보낸다.
      if (isMobileViewport()) {
        goBackToInboxEntryFromDeletedInboxRoute();
      } else {
        replaceAppRoute('/home');
      }
    },
  });

  return true;
}

function isLearnDetailRoute(pathname: string) {
  return /^\/(?:[^/]+\/)?learn\/[^/]+$/.test(pathname);
}

function isDeletedInboxLearnDetailApi(status: number, apiUrl: string, apiMethod: string) {
  const path = getApiPath(apiUrl);
  const basicApi = /\/learn\/learner\/online\/[^/]+$/.test(path);
  const coursesApi = /\/learn\/learner\/online\/[^/]+\/courses$/.test(path);
  const startApi = /\/learn\/learner\/online\/[^/]+\/start$/.test(path);

  return (
    (status === 500 && apiMethod === 'POST' && startApi) ||
    (status === 404 && apiMethod === 'GET' && (basicApi || coursesApi))
  );
}

/**
 * 전역 API 에러 핸들러는 React 컴포넌트 밖에서 실행되므로 `useResponsive` 같은 hook을 사용할 수 없다.
 * 프로젝트에서 모바일 판단에 주로 쓰는 `down('md')` 기준과 맞추기 위해 MUI 기본 md 기준(900px 미만)을 직접 사용한다.
 */
function isMobileViewport() {
  if (typeof window === 'undefined') return false;
  if (typeof window.matchMedia === 'function') {
    return window.matchMedia('(max-width: 899.95px)').matches;
  }

  return window.innerWidth < 900;
}

/**
 * 인박스에서 삭제/확인 불가 콘텐츠로 진입했다가 에러 알러트를 확인한 경우,
 * 새 `/inbox` route를 만들지 않고 기존 인박스 history entry로 되돌린다.
 *
 * 인박스 링크 클릭 후 에러 알러트가 열린 시점의 history 구조는 보통 아래와 같다.
 *
 * 1. 이전 화면
 * 2. 인박스
 * 3. 실패한 상세 URL (`/learn/:id?...entry=inbox`, `/degree/:id?...entry=inbox`)
 * 4. 실패한 상세 URL + modal history state
 *
 * 여기서 `/inbox`로 replace/assign 하면 4번만 인박스로 바뀌고 3번 실패 URL이 남는다.
 * 그 상태에서 인박스 X 버튼은 history back을 사용하므로 다시 실패 URL로 돌아가 라우트가 갇힌다.
 *
 * 따라서 모바일 인박스 삭제 콘텐츠 전용 플로우에서는 `history.go(-2)`로
 * 모달 entry와 실패 URL entry를 함께 되돌려 기존 인박스를 현재 위치로 만든다.
 * `skipNextModalHistoryCleanup()`은 알러트가 닫히며 모달 훅이 추가 `history.back()`을 실행하지 않도록 막는다.
 */
function goBackToInboxEntryFromDeletedInboxRoute() {
  const hasModalHistory = window.history.state?.__modal;

  if (hasModalHistory && window.history.length > 2) {
    skipNextModalHistoryCleanup();
    window.history.go(-2);
    return;
  }

  if (window.history.length > 1) {
    skipNextModalHistoryCleanup();
    window.history.back();
    return;
  }

  replaceAppRoute('/inbox');
}

/**
 * 멀티테넌시 basename을 보존한 내부 라우트 replace 이동을 수행한다.
 *
 * `handleApiError`는 React Router hook 영역 밖에서 실행되므로 `navigate`나 `useRoute`를 사용할 수 없다.
 * 이때 `window.location.assign('/inbox')`처럼 origin 기준 절대 경로로 이동하면
 * 스테이지/운영의 `/:tenant/inbox` 구조에서 tenant segment가 사라진다.
 *
 * 현재 path에서 추출한 tenant가 런타임 설정에 존재하면 React Router basename으로 간주해 목적지 앞에 붙인다.
 *
 * 이 함수는 라우터 hook을 사용할 수 없는 전역 에러 처리에서 최종 목적지로 강제 이동할 때만 사용한다.
 * 모바일 인박스 삭제 콘텐츠처럼 기존 인박스 history entry로 되돌아가야 하는 플로우는
 * `goBackToInboxEntryFromDeletedInboxRoute()`에서 별도로 처리한다.
 *
 * 이 이동은 이미 웹뷰 내부에서 에러 다이얼로그 확인 후 복귀하는 흐름이므로
 * 푸시/외부 링크 진입용 `linkType=INTERNAL_LINK` 쿼리를 붙이지 않는다.
 * 해당 쿼리를 붙이면 `useRedirectPath`가 딥링크 유입으로 해석해 `entry=deeplink`를 추가할 수 있다.
 *
 * @param routePath 앱 내부 목적지. 반드시 `/inbox`, `/home`처럼 slash로 시작하는 앱 라우트여야 한다.
 */
function replaceAppRoute(routePath: '/inbox' | '/home') {
  window.location.replace(`${getCurrentRouteBasePath()}${routePath}`);
}

/**
 * 현재 브라우저 URL에서 React Router basename에 해당하는 tenant/base path를 추출한다.
 *
 * 라우트 목록을 수동으로 들고 있으면 새 라우트 추가 시 누락될 수 있으므로,
 * 앱 부팅 시 `BrowserRouter basename` 계산에 쓰는 tenant 추출 유틸을 그대로 재사용한다.
 *
 * 예:
 * - `/learn/4686?entry=inbox` -> `''`
 * - `/anchor/learn/4686?entry=inbox` -> `/anchor`
 * - `/brand-a/learn/4686?entry=inbox` -> `/brand-a`
 *
 * @returns 현재 URL에 포함된 tenant/base prefix. 없는 경우 빈 문자열.
 */
function getCurrentRouteBasePath() {
  const tenant = getTenantRedirectUri();
  if (!tenant) return '';
  if (!getRuntimeConfig()) return '';

  return `/${tenant}`;
}

function getApiPath(apiUrl: string) {
  try {
    return new URL(apiUrl, window.location.origin).pathname;
  } catch {
    return apiUrl.split('?')[0] || '';
  }
}

// eslint-disable-next-line @emcast/custom-rules/no-any-in-api
export const handleApiError = (error: any) => {
  const baseURL = error?.config?.baseURL ?? '';
  const url = error?.config?.url ?? '';
  const apiUrl = `${baseURL}${url}`;
  const apiMethod = String(error?.config?.method ?? '').toUpperCase();

  const { response } = error;

  // 응답 객체가 없을 경우 (네트워크 에러, CORS 차단 등)
  if (!response) {
    // GA/GTM 에러 수집은 분석용 부가 로직입니다.
    // pushLearnerError 내부가 try/catch로 보호되어 있어 수집 실패가 화면 오류로 번지지 않습니다.
    pushLearnerError({
      error_type: 'network',
      api_path: sanitizeApiPath(apiUrl),
      api_method: apiMethod,
      screen_name: window.location?.pathname,
      handled: true,
    });
    console.log('네트워크 에러 혹은 서버와의 연결 실패');
    return true; // ErrorBoundary로 전파하지 않음
  }

  const { status, data } = response;
  const pathname = window.location?.pathname;
  const search = window.location?.search;
  const errMsg = data?.meta?.errMsg || '';

  // 자동로그인 파라미터 체크, 센트리 로그 수집
  const isAutoLogin = window.location.search.includes('autoLogin');

  console.group('HandleApiError - HTTP STATUS, Error Data');
  console.log(`HTTP Status : `, status ?? 'HTTP STATUS 없음');
  console.log(`Error Data : `, data ?? 'DATA 없음');
  console.log('isAutoLogin : ', isAutoLogin);
  console.groupEnd();

  const knownResult = resolveKnownApiErrorHandling(
    status,
    data,
    apiUrl,
    apiMethod,
    pathname,
    search
  );
  if (knownResult !== undefined) {
    // knownResult=false는 로그인 등 호출부가 직접 처리하는 비즈니스 에러입니다.
    // 호출부에서 더 정확한 action_name으로 수집해야 하므로 여기서는 중복 전송하지 않습니다.
    if (knownResult !== false) {
      // 401, 403/404 예외, 429처럼 공통 처리된 에러도 발생 빈도 분석을 위해 수집합니다.
      // 단, 서버 응답 메시지 원문과 stack trace는 개인정보/민감정보 가능성이 있어 보내지 않습니다.
      pushLearnerError({
        error_type: 'api',
        http_status: status,
        error_code: getErrorCode(data),
        api_path: sanitizeApiPath(apiUrl),
        api_method: apiMethod,
        screen_name: pathname,
        handled: knownResult,
      });
    }
    return knownResult;
  }

  // 그 외 에러는 공통 모달 표시
  openModal('apiError', { errorMessage: errMsg });

  // 상태코드 별 로깅 처리
  logHttpStatus(status, data);

  // 공통 처리되지 않은 API 에러는 모달 노출 후 수집합니다.
  // errMsg/errTranslate 같은 서버 메시지 원문은 GA4로 보내지 않습니다.
  pushLearnerError({
    error_type: 'api',
    http_status: status,
    error_code: getErrorCode(data),
    api_path: sanitizeApiPath(apiUrl),
    api_method: apiMethod,
    screen_name: pathname,
    handled: true,
  });

  return true;
};

/**
 * [HTTP 상태코드별 로그 출력 및 추가 처리]
 * - Sentry와 별도로 콘솔에 에러 기록
 * - 특정 상태에 따라 추가 처리도 수행
 */
// eslint-disable-next-line @emcast/custom-rules/no-any-in-api
const logHttpStatus = (status: number, data: any) => {
  switch (status) {
    case 400:
      console.log('400 ERROR');
      if (data?.meta?.errCode) {
        console.log(`Error Code: ${data.meta.errCode}`);
        handleMetaCodeError(data.meta.errCode); // 커스텀 에러 코드에 대한 추가 처리
      }
      return false;

    case 401:
      console.log('401 ERROR');
      localStorageUtil.removeToken(); // 혹시 삭제 누락 방지
      break;

    default:
      console.log('알 수 없는 에러가 발생했습니다.');
      break;
  }
};

/**
 * [서버 커스텀 에러코드 핸들링]
 * - 명확하게 정의된 에러코드에 대한 분기 처리
 * - 보안 정책상 사용자에게 노출하지 않거나, 내부적으로 특별 대응이 필요한 경우
 */
const handleMetaCodeError = (errCode: number) => {
  switch (errCode) {
    case 1200:
      console.log('1200 error');
      break;
    case 1201:
      console.log('1201 error');
      break;
    default:
      console.log('Error Code : ', errCode);
      break;
  }
};

/**
 * [서버 커스텀 에러코드 필터링]
 * - UI에 표시할 필요 없는 에러코드는 여기서 필터링
 * - 무시할 수 있는 에러코드는 이후 처리 생략
 */
function isSpecificErrorCode(errCode: number | undefined): boolean {
  const specificErrorCodes = [
    1200, 1201, 1202, 1204, 1205, 1206, 1207, 1105, 1335, 1233, 1100, 1523, 1004, 7401, 8809,
  ];
  return specificErrorCodes.includes(errCode ?? -1);
}

/** 체크리스트 학습자 작성 차단(예: meta.errCode 3003) */
function isChecklistSupervisorOnlyError(apiUrl: string, data: ApiErrorResponseBody) {
  if (!apiUrl.includes('/checklist/')) return false;
  const errCode = data?.meta?.errCode ?? data?.errCode;
  return errCode === 3003;
}

/**
 * [403 예외 처리]
 * - 홈 화면, 위젯 보드 API 등에서는 권한 없음 에러를 사용자에게 보여주지 않음
 */
function is403Exception(status: number, apiUrl: string, pathString: string): boolean {
  if (status !== 403) return false;

  const isWidgetBoardApi = apiUrl.includes('/corporation/brand/widget-board');
  const isHomeRoute = pathString === '/home' || /^\/[^/]+\/home$/.test(pathString);

  if (isWidgetBoardApi) {
    console.log('403 ERROR for widget-board API: 다이얼로그 표시 제외');
    return true;
  }

  if (isHomeRoute) {
    console.log(`403 ERROR on ${pathString}: 홈 화면에서는 다이얼로그 표시 제외`);
    return true;
  }

  return false;
}

/**
 * [404 예외 처리]
 * - 홈 화면, 위젯 보드 API 등에서는 Not Found 에러도 사용자에게 표시하지 않음
 */
function is404Exception(status: number, apiUrl: string, pathString: string): boolean {
  if (status !== 404) return false;

  const isWidgetBoardApi = apiUrl.includes('/corporation/brand/widget-board');
  const isHomeRoute = pathString === '/home' || /^\/[^/]+\/home$/.test(pathString);

  if (isWidgetBoardApi) {
    console.log('404 ERROR for widget-board API: 다이얼로그 표시 제외');
    return true;
  }

  if (isHomeRoute) {
    console.log(`404 ERROR on ${pathString}: 홈 화면에서는 다이얼로그 표시 제외`);
    return true;
  }

  return false;
}

/**
 * [401 처리]
 * - 인증 만료나 실패 시 처리
 * - 토큰 제거 후 로그인 페이지로 리다이렉션
 */
// eslint-disable-next-line @emcast/custom-rules/no-any-in-api
const is401Exception = (status: any) => {
  if (status === 401) {
    localStorageUtil.removeToken();
    window.location.href = makeRedirectUrl();
    return true;
  }
};

/**
 * [온라인 클래스 전용 400, 404 예외 처리]
 * - 온라인 클래스 상세 400에러 핸들링
 * - 캠페인 배너 이미지 등이 없는 경우에도 UI에서는 무시해야 함
 */
// eslint-disable-next-line @emcast/custom-rules/no-any-in-api
const isDegreeDetailExcept = (status: any, apiUrl: string, data: any) => {
  if (status === 404 && apiUrl.includes('/learn/online')) {
    // 1. 인박스에서 들어온 에러
    if (window.location.search.includes('entry=inbox')) {
      openModal('alert', {
        content: i18next.t('learner.5013'),
        accept: i18next.t('learner.5014'),
        disableBackdropClick: true,
        onAccept: () => {
          // 모바일은 inbox로 보내고, 데스크탑은 home으로 보낸다.
          if (isMobileViewport()) {
            goBackToInboxEntryFromDeletedInboxRoute();
          } else {
            replaceAppRoute('/home');
          }
        },
      });
      return true;
    }

    // 2. 일반 에러
    openModal('alert', {
      content: data?.meta?.errMsg ?? '',
      disableBackdropClick: true,
      onAccept: () => {},
    });
    return true;
  }
  if (status === 404 && apiUrl.includes('campaign')) return true;
};
